/**
 * Живой предпросмотр сообщения, которое уйдёт в рабочий чат (§6.3, §6.5).
 *
 * Текст собирает сервер — тем же кодом, которым отправляет в Telegram
 * (`api/src/modules/telegram/format.ts`). Раньше сборка была продублирована
 * здесь, и дубль неизбежно отстал: в чат уходил рассказ по брендам, а на
 * ревью показывались старые разделы «URL и статусы» и «Комментарии», где
 * закрытая задача выглядела двумя разными делами. Предпросмотр, который
 * врёт о сообщении, хуже отсутствующего — поэтому сериализатор один.
 *
 * Разметку Telegram (`<b>`, `<a>`) разбираем сами: вставлять чужой HTML в
 * страницу нельзя, а жирные имена брендов в предпросмотре нужны.
 */
import { useEffect, useState, type ReactNode } from 'react'
import type { Change } from '@shared/types.ts'
import { IconChevronDown, IconChevronRight } from '../../components/index.js'
import { api } from '../../lib/api.js'
import { draftTexts } from './texts.draft.js'
import './TelegramPreview.css'

/** Пауза перед перезапросом: заметку к пакету набирают по букве. */
const DEBOUNCE_MS = 300

export interface TelegramPreviewProps {
  /** Правки черновика: по ним пересобирается предпросмотр. */
  changes: Change[]
  /** Заметка к пакету как она набрана сейчас — ещё до сохранения. */
  note: string
  /** Выключенные уведомления — честная строка под предпросмотром. */
  enabled: boolean
}

export function TelegramPreview({ changes, note, enabled }: TelegramPreviewProps) {
  const [open, setOpen] = useState(true)
  const [parts, setParts] = useState<string[]>([])
  const [nothing, setNothing] = useState(false)
  const [failed, setFailed] = useState(false)

  const signature = changes.map((change) => change.id).join(',')

  useEffect(() => {
    if (!open) return
    let alive = true
    const timer = setTimeout(() => {
      api.draftPreview(note.trim())
        .then((preview) => {
          if (!alive) return
          setNothing(preview.empty)
          setParts(preview.empty
            ? []
            : preview.parts.length > 0 ? preview.parts : preview.text ? [preview.text] : [])
          setFailed(false)
        })
        .catch(() => { if (alive) setFailed(true) })
    }, DEBOUNCE_MS)
    return () => { alive = false; clearTimeout(timer) }
  }, [open, signature, note])

  return (
    <section className="tgpreview">
      <button
        type="button"
        className="tgpreview__toggle t-cell"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="tgpreview__chevron" aria-hidden="true">
          {open ? <IconChevronDown /> : <IconChevronRight />}
        </span>
        {draftTexts.telegram.toggle}
      </button>

      {open ? (
        <>
          <div className="tgpreview__box t-cell" role="group" aria-label={draftTexts.telegram.aria}>
            {/* Прежний текст остаётся на экране, пока идёт перезапрос: мигание
                пустотой после каждой буквы в заметке читается как поломка. */}
            {parts.length === 0
              ? (
                <p className="tgpreview__line tgpreview__line--muted">
                  {nothing ? draftTexts.telegram.nothing : draftTexts.telegram.loading}
                </p>
              )
              : parts.map((part, partIndex) => (
                  <div className="tgpreview__part" key={partIndex}>
                    {partIndex > 0 ? (
                      <p className="tgpreview__split t-meta">{draftTexts.telegram.nextMessage}</p>
                    ) : null}
                    {part.split('\n').map((line, index) => (
                      line === ''
                        ? <span className="tgpreview__gap" key={index} />
                        : <p className="tgpreview__line" key={index}>{renderLine(line)}</p>
                    ))}
                  </div>
                ))}
          </div>
          <p className="tgpreview__note t-meta">
            {failed
              ? draftTexts.telegram.failed
              : enabled ? draftTexts.telegram.numberFixed : draftTexts.telegram.disabled}
          </p>
        </>
      ) : null}
    </section>
  )
}

// ───────────────────── разметка Telegram без вставки HTML ────────────────────

/** В сообщении встречаются только `<b>…</b>` и `<a href="…">…</a>`. */
const TAG = /<(b|a)(?:\s+href="([^"]*)")?>([\s\S]*?)<\/\1>/g

function renderLine(line: string): ReactNode {
  const nodes: ReactNode[] = []
  let last = 0
  let key = 0
  for (const match of line.matchAll(TAG)) {
    const at = match.index ?? 0
    if (at > last) nodes.push(unescapeHtml(line.slice(last, at)))
    const inner = unescapeHtml(match[3] ?? '')
    nodes.push(match[1] === 'b'
      ? <b key={key++}>{inner}</b>
      : <span className="tgpreview__link" key={key++}>{inner}</span>)
    last = at + match[0].length
  }
  if (last < line.length) nodes.push(unescapeHtml(line.slice(last)))
  return nodes.length > 0 ? nodes : line
}

function unescapeHtml(value: string): string {
  return value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
}
